import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1,
  Volume2, VolumeX, ChevronDown, X, ArrowUp, ArrowDown, ListMusic, MicVocal,
} from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { getTrackPage } from "../lib/api";

const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function QueuePanel() {
  const { queue, qIndex, playQueueIndex, removeFromQueue, moveInQueue } = usePlayer();
  return (
    <div data-testid="player-queue-panel">
      {queue.length === 0 && (
        <p className="font-mono text-[10px] tracking-[0.25em] text-white/30 py-8">THE QUEUE IS EMPTY.</p>
      )}
      {queue.map((e, i) => {
        const active = i === qIndex;
        return (
          <div key={`${e.t.slug || e.t.trackNumber}-${i}`}
            className={`flex items-center gap-3 border-t border-white/10 py-3 ${active ? "bg-gold/5" : ""}`}
            data-testid={`queue-item-${i}`}>
            <button onClick={() => playQueueIndex(i)} data-testid={`queue-item-${i}-play`}
              className="flex-1 min-w-0 text-left group">
              <p className={`font-serif text-lg truncate transition-colors ${active ? "text-gold" : "text-white/85 group-hover:text-gold"}`}>
                <span className="font-mono text-[9px] text-white/30 mr-3">{String(i + 1).padStart(2, "0")}</span>
                {e.t.title}
              </p>
              <p className="font-mono text-[8px] tracking-[0.25em] text-white/30 mt-0.5 truncate">
                {(e.ctx?.releaseTitle || "").toUpperCase()}
                {["TEASER", "PREVIEW"].includes(e.t.availability) ? " · TEASER" : ""}
              </p>
            </button>
            <span className="font-mono text-[9px] text-white/30 shrink-0">{e.t.duration}</span>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => moveInQueue(i, -1)} disabled={i === 0} aria-label="Move up"
                data-testid={`queue-item-${i}-up`}
                className="text-white/30 hover:text-white disabled:opacity-20 p-1"><ArrowUp size={12} /></button>
              <button onClick={() => moveInQueue(i, 1)} disabled={i === queue.length - 1} aria-label="Move down"
                data-testid={`queue-item-${i}-down`}
                className="text-white/30 hover:text-white disabled:opacity-20 p-1"><ArrowDown size={12} /></button>
              {!active && (
                <button onClick={() => removeFromQueue(i)} aria-label="Remove from queue"
                  data-testid={`queue-item-${i}-remove`}
                  className="text-white/30 hover:text-red-400 p-1"><X size={12} /></button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LyricsPanel() {
  const { current } = usePlayer();
  const [state, setState] = useState({ slug: null, lyrics: null, loading: false });
  const [auto, setAuto] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [userPaused, setUserPaused] = useState(false);
  const boxRef = useRef(null);
  const rafRef = useRef(null);
  const accRef = useRef(0);

  const slug = current?.trackSlug;

  useEffect(() => {
    if (!slug) { setState({ slug: null, lyrics: null, loading: false }); return; }
    setState({ slug, lyrics: null, loading: true });
    getTrackPage(slug)
      .then((d) => setState({ slug, lyrics: d.editorial?.lyrics || null, loading: false }))
      .catch(() => setState({ slug, lyrics: null, loading: false }));
    if (boxRef.current) boxRef.current.scrollTop = 0;
  }, [slug]);

  const running = auto && !userPaused;
  useEffect(() => {
    if (!running) { cancelAnimationFrame(rafRef.current); return; }
    const step = () => {
      const el = boxRef.current;
      if (el) {
        accRef.current += speed * 0.4;
        if (accRef.current >= 1) {
          const px = Math.floor(accRef.current);
          accRef.current -= px;
          el.scrollTop += px;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [running, speed]);

  const onManual = useCallback(() => { if (auto) setUserPaused(true); }, [auto]);

  return (
    <div data-testid="player-lyrics-panel">
      <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-white/10">
        <button onClick={() => { setAuto(!auto); setUserPaused(false); }} data-testid="lyrics-autoscroll-toggle"
          aria-pressed={auto}
          className={`font-mono text-[9px] tracking-[0.2em] border px-4 py-2 transition-colors ${
            auto ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/45 hover:border-white/40"}`}>
          AUTO-SCROLL {auto ? "ON" : "OFF"}
        </button>
        {auto && userPaused && (
          <button onClick={() => setUserPaused(false)} data-testid="lyrics-autoscroll-resume"
            className="font-mono text-[9px] tracking-[0.2em] border border-gold/50 text-gold px-4 py-2">
            RESUME
          </button>
        )}
        {auto && !userPaused && (
          <button onClick={() => setUserPaused(true)} data-testid="lyrics-autoscroll-pause"
            className="font-mono text-[9px] tracking-[0.2em] border border-white/15 text-white/45 px-4 py-2 hover:border-white/40">
            PAUSE
          </button>
        )}
        {auto && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="lyrics-speed-controls">
            {SPEEDS.map((s) => (
              <button key={s} onClick={() => setSpeed(s)} data-testid={`lyrics-speed-${String(s).replace(".", "-")}`}
                className={`font-mono text-[9px] px-2.5 py-1.5 border transition-colors ${
                  speed === s ? "border-gold text-gold" : "border-white/10 text-white/35 hover:border-white/30"}`}>
                {s}×
              </button>
            ))}
            <input type="range" min={0.25} max={2.5} step={0.25} value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              data-testid="lyrics-speed-slider" aria-label="Lyrics scroll speed"
              className="w-24 h-[3px] appearance-none bg-white/15 accent-[#d4af37] cursor-pointer" />
          </div>
        )}
      </div>
      {auto && (
        <p className="font-mono text-[8px] tracking-[0.25em] text-white/25 mt-3">
          CONTINUOUS SCROLL — NOT TIME-SYNCED TO THE RECORDING.
        </p>
      )}
      <div ref={boxRef} onWheel={onManual} onTouchMove={onManual}
        className="mt-5 max-h-[42vh] lg:max-h-[46vh] overflow-y-auto pr-3" data-testid="lyrics-scroll-box">
        {state.loading ? (
          <p className="font-mono text-[10px] tracking-[0.25em] text-white/30 py-8">RECEIVING…</p>
        ) : state.lyrics ? (
          <p className="font-serif text-xl md:text-2xl font-light leading-relaxed text-white/85 whitespace-pre-line pb-24"
            data-testid="lyrics-text">{state.lyrics}</p>
        ) : (
          <p className="font-mono text-[10px] tracking-[0.25em] text-white/30 py-8" data-testid="lyrics-empty">
            LYRICS NOT YET PUBLISHED.
          </p>
        )}
      </div>
    </div>
  );
}

export default function ExpandedPlayer() {
  const { current, playing, muted, volume, progress, duration, queue, shuffle, repeat,
    toggle, next, prev, seek, toggleMute, setVolume, toggleShuffle, cycleRepeat, setExpanded } = usePlayer();
  const [tab, setTab] = useState("queue");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [setExpanded]);

  if (!current) return null;
  const RepeatIcon = repeat === "track" ? Repeat1 : Repeat;

  return (
    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-0 z-[9990] bg-[#050505]/[0.985] backdrop-blur-2xl overflow-y-auto"
      data-testid="expanded-player">
      <div className="mm-vignette pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 md:px-12 py-6 md:py-10 min-h-full flex flex-col">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[9px] tracking-[0.45em] text-gold">NOW PLAYING</p>
          <button onClick={() => setExpanded(false)} data-testid="expanded-player-close" aria-label="Minimize player"
            className="w-10 h-10 rounded-full border border-white/20 text-white/60 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
            <ChevronDown size={17} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-8 md:mt-10 flex-1">
          {/* LEFT — artwork + transport */}
          <div className="flex flex-col">
            <div className="max-w-[420px]">
              {current.artwork ? (
                <img src={current.artwork} alt={`${current.title} artwork`}
                  className="w-full aspect-square object-cover rounded-[16px] ring-1 ring-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
                  data-testid="expanded-player-artwork" />
              ) : (
                <div className="w-full aspect-square rounded-[16px] bg-[#0a0a0a] ring-1 ring-white/10 flex items-center justify-center">
                  <ListMusic size={40} className="text-white/15" />
                </div>
              )}
            </div>
            <div className="mt-7">
              {current.trackSlug ? (
                <Link to={`/track/${current.trackSlug}`} onClick={() => setExpanded(false)}
                  data-testid="expanded-player-title"
                  className="font-serif text-3xl md:text-4xl font-light text-white hover:text-gold transition-colors break-words">
                  {current.title}
                </Link>
              ) : (
                <p className="font-serif text-3xl md:text-4xl font-light text-white break-words" data-testid="expanded-player-title">{current.title}</p>
              )}
              <p className="font-mono text-[9px] tracking-[0.3em] text-white/40 mt-3">
                {(current.releaseTitle || "").toUpperCase()}
                {current.dimension ? ` · DIMENSION ${current.dimension}` : ""}
              </p>
              {current.teaser && (
                <span className="inline-block mt-3 font-mono text-[8px] tracking-[0.3em] text-gold/80 border border-gold/30 px-2.5 py-1">
                  TEASER{current.teaserDuration ? ` · ${current.teaserDuration}″` : ""}
                </span>
              )}
            </div>

            {/* seek */}
            <div className="flex items-center gap-3 mt-7 max-w-[480px]">
              <span className="font-mono text-[9px] text-white/40 w-9">{fmt(progress)}</span>
              <input type="range" min={0} max={duration || 0} value={progress} step={0.1}
                onChange={(e) => seek(Number(e.target.value))}
                data-testid="expanded-player-seek" aria-label="Seek"
                className="flex-1 h-[4px] appearance-none bg-white/15 accent-[#d4af37] cursor-pointer" />
              <span className="font-mono text-[9px] text-white/40 w-9">{fmt(duration)}</span>
            </div>

            {/* transport */}
            <div className="flex items-center gap-4 md:gap-5 mt-6">
              <button onClick={toggleShuffle} data-testid="expanded-player-shuffle" aria-pressed={shuffle} aria-label="Shuffle"
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
                  shuffle ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                <Shuffle size={14} />
              </button>
              <button onClick={prev} data-testid="expanded-player-prev" aria-label="Previous"
                disabled={queue.length < 2 && progress <= 4}
                className="w-11 h-11 rounded-full border border-white/20 text-white/70 flex items-center justify-center hover:border-gold hover:text-gold transition-colors disabled:opacity-30">
                <SkipBack size={16} />
              </button>
              <button onClick={toggle} data-testid="expanded-player-toggle" aria-label={playing ? "Pause" : "Play"}
                className="w-14 h-14 rounded-full bg-gold text-black flex items-center justify-center hover:bg-white transition-colors">
                {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
              </button>
              <button onClick={next} data-testid="expanded-player-next" aria-label="Next"
                disabled={queue.length < 2}
                className="w-11 h-11 rounded-full border border-white/20 text-white/70 flex items-center justify-center hover:border-gold hover:text-gold transition-colors disabled:opacity-30">
                <SkipForward size={16} />
              </button>
              <button onClick={cycleRepeat} data-testid="expanded-player-repeat" aria-label={`Repeat: ${repeat}`}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
                  repeat !== "off" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                <RepeatIcon size={14} />
              </button>
            </div>

            {/* volume */}
            <div className="flex items-center gap-3 mt-6 max-w-[280px]">
              <button onClick={toggleMute} data-testid="expanded-player-mute" aria-label={muted ? "Unmute" : "Mute"} aria-pressed={muted}
                className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors shrink-0 ${
                  muted ? "border-gold/60 text-gold" : "border-white/20 text-white/60 hover:border-white/50"}`}>
                {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
              </button>
              <input type="range" min={0} max={1} step={0.02} value={muted ? 0 : volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                data-testid="expanded-player-volume" aria-label="Volume"
                className="flex-1 h-[3px] appearance-none bg-white/15 accent-[#d4af37] cursor-pointer" />
            </div>
          </div>

          {/* RIGHT — queue / lyrics */}
          <div className="pb-16 lg:pb-0">
            <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <button onClick={() => setTab("queue")} data-testid="expanded-player-tab-queue"
                className={`flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] px-5 py-2.5 border transition-colors ${
                  tab === "queue" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/45 hover:border-white/40"}`}>
                <ListMusic size={12} /> QUEUE {queue.length > 0 && `(${queue.length})`}
              </button>
              <button onClick={() => setTab("lyrics")} data-testid="expanded-player-tab-lyrics"
                className={`flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] px-5 py-2.5 border transition-colors ${
                  tab === "lyrics" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/45 hover:border-white/40"}`}>
                <MicVocal size={12} /> LYRICS
              </button>
            </div>
            <div className="mt-4">
              {tab === "queue" ? <QueuePanel /> : <LyricsPanel />}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
